import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import type { AppState } from '@/store/store.ts';
import { getCheckTargetAccumulatorBody } from '@/store/targetAccumulator/api/tradeCapture/request.ts';
import type { TargetAccumulatorResponse } from '@/store/targetAccumulator/api/tradeCapture/response.model.ts';
import {
  mapTcResponse,
  type TargetAccumulatorTcResponse,
} from '@/store/targetAccumulator/api/tradeCapture/response.ts';
import type { TargetAccumulatorProperties, TargetAccuType } from '@/store/targetAccumulator/state.ts';

export type Nullable<T> = {
  [P in keyof T]: T[P] extends string | number | boolean | undefined ? T[P] | null : T[P];
};

export type NullableTargetAccumulatorProperties = Partial<Nullable<TargetAccumulatorProperties>>;

export interface CheckTargetAccumulatorParams {
  currencyPair?: string | null;
  tileId: string;
  properties?: NullableTargetAccumulatorProperties;
  accuType?: TargetAccuType | null;
}

export const checkTargetAccumulator = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<TargetAccumulatorTcResponse, CheckTargetAccumulatorParams>({
    queryFn: async (params, api, _extraOptions, baseQuery) => {
      const state = api.getState() as AppState;
      const body = getCheckTargetAccumulatorBody(params, state);

      const tradeCapture = state.targetAccumulator?.[params.tileId]?.tradeCapture;
      // If there was a previous Trade Capture error, we should not attempt to check the Target Accumulator
      if (tradeCapture?.status === 'ERROR') {
        return {
          error: {
            status: 'CUSTOM_ERROR',
            error: tradeCapture.error,
            message: 'Cannot check Target Accumulator due to previous Trade Capture error',
          },
        };
      }

      const result = await baseQuery({
        url: '/tradeCaptureBatch/Accumulator',
        method: 'POST',
        body,
        timeout: 10_000, // 10 seconds
      });

      if (result.error) {
        return { error: result.error };
      }

      const response = result.data as TargetAccumulatorResponse;
      const tileId = params.tileId;
      const currencyPair = params.currencyPair;
      const accuType = params.accuType;
      const sessionId = state.targetAccumulator[params.tileId]?.tradeCapture.sessionId;

      const transformedData = mapTcResponse(response, tileId, sessionId, currencyPair, accuType);
      return { data: transformedData };
    },
  });
};
